import React from 'react';
import { useHealthScore } from '../../hooks/useHealthScore';
import CircularProgress from '../ui/CircularProgress';
import ScoreBar from '../ui/ScoreBar';

const CATEGORY_LABELS = {
  sleep: 'Sleep',
  exercise: 'Exercise',
  nutrition: 'Nutrition',
  biometric: 'Biometrics',
  mental_health: 'Mental Health',
  therapy: 'Therapy',
};

const LEFT_CATEGORIES = ['sleep', 'exercise', 'nutrition'];
const RIGHT_CATEGORIES = ['biometric', 'mental_health', 'therapy'];

// A quiet "what would sharpen it" line. Coverage-first: if there are areas
// with no data, adding one broadens the picture. Otherwise, nudge the weakest
// tracked area. No tiers, no unlocks — just the next honest step.
function getSharpenHint(scoreData) {
  if (!scoreData || scoreData.overall_score == null) return null;

  const { coverage, category_scores } = scoreData;

  if (coverage?.missing?.length > 0) {
    return `Add ${coverage.missing[0]} to widen your picture`;
  }

  if (category_scores && Object.keys(category_scores).length > 0) {
    const lowest = Object.entries(category_scores)
      .sort(([, a], [, b]) => a.score - b.score)[0];
    if (lowest && lowest[1].score < 80) {
      return `Improve ${CATEGORY_LABELS[lowest[0]] || lowest[0]} to raise your score`;
    }
  }

  if (scoreData.overall_score >= 90) return 'Outstanding — keep it up!';
  return null;
}

const HealthScoreCard = ({ refreshKey }) => {
  const { scoreData, loading } = useHealthScore(refreshKey);

  if (loading) {
    return (
      <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-5 animate-pulse">
        <div className="flex items-center gap-5">
          <div className="w-[116px] h-[116px] rounded-full bg-gray-200 dark:bg-gray-700 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24" />
          </div>
        </div>
      </div>
    );
  }

  const isNewUser = !scoreData || scoreData.overall_score == null;
  const hint = getSharpenHint(scoreData);
  const coverage = scoreData?.coverage;

  return (
    <div className="card-glow rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-5 transition-shadow hover:shadow-card">
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
        {/* Score ring */}
        <div className="shrink-0">
          <CircularProgress score={isNewUser ? null : scoreData.overall_score} />
        </div>

        {/* Details */}
        <div className="flex-1 w-full min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-0.5">
            {isNewUser ? 'Getting Started' : 'Health score'}
          </h3>

          {isNewUser ? (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Start logging health data to see your score
            </p>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Scored on {coverage?.scored_on ?? 0} of {coverage?.total ?? 6} areas
            </p>
          )}

          {/* Category bars — two columns */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            {[0, 1, 2].map((i) => {
              const leftSlug = LEFT_CATEGORIES[i];
              const rightSlug = RIGHT_CATEGORIES[i];
              const leftScore = scoreData?.category_scores?.[leftSlug];
              const rightScore = scoreData?.category_scores?.[rightSlug];
              return (
                <React.Fragment key={i}>
                  <ScoreBar
                    label={CATEGORY_LABELS[leftSlug]}
                    score={leftScore ? leftScore.score : 0}
                    active={leftScore != null}
                  />
                  <ScoreBar
                    label={CATEGORY_LABELS[rightSlug]}
                    score={rightScore ? rightScore.score : 0}
                    active={rightScore != null}
                  />
                </React.Fragment>
              );
            })}
          </div>

          {hint && (
            <p className="mt-2.5 text-sm text-primary-600 dark:text-primary-400">
              {hint}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthScoreCard;
